import {
	Button,
	ErrorMessageInput,
	Icon,
	Separator,
	Typography,
} from 'components/common';
import InputPassword from 'components/form/input-password/input-password';
import { InputEmail } from 'components/form';
import NextAuthProvidersEnum from 'const/auth';
import Icons from 'const/icons';
import AppRoutes from 'const/routes';
import Link from 'next/link';
import React from 'react';
import { passwordCriteria } from '@/const';
import { LayoutSignUp } from '@/components/layout';
import clsx from 'clsx';
import dynamic from 'next/dynamic';
import { useAuth } from '@/hooks/useAuth';
import { SignUpRightPanel } from './SignUpRightPanel';

const Recaptcha = dynamic(
	() => import('@/components/common/recaptcha/ReCaptchaComponent'),
	{ ssr: false }
);

export const ClientSignUpScreen: React.FC = () => {
	const {
		form,
		isLoading,
		isStaging,
		isDev,
		recaptchaValid,
		handleSubmitData,
		onChangeRecaptcha,
	} = useAuth();

	const {
		register,
		handleSubmit,
		formState: { errors, isDirty, isValid },
		setValue,
	} = form;

	const rules = {
		email: {
			required: { value: true, message: 'This is required.' },
		},
		password: {
			required: { value: true, message: 'This is required.' },
			maxLength: {
				value: 30,
				message: 'Your password must be 30 characters or less',
			},
			minLength: {
				value: 8,
				message:
					'Your password does not meet the minimum requirements. It must be at least 8 characters long and include one number, one uppercase letter, and one special character (_-:*).',
			},
			validate: (value: string) => {
				const passwordRegex = /^(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;
				if (!passwordRegex.test(value)) {
					return 'Your password does not meet the minimum requirements. It must be at least 8 characters long and include one number, one uppercase letter, and one special character (_-:*).';
				}
				return undefined;
			},
		},
	};

	return (
		<LayoutSignUp
			rightContent={<SignUpRightPanel />}
			classNameLeft="pt-8 lg:pt-0"
		>
			<div className="w-full lg:max-w-[556px]">
				<Typography type="headline-3" className="text-black font-bold mb-2">
					Create your account <br /> to check if you qualify
				</Typography>
				<Typography type="regular-16" className="text-[#6B7280] mb-8">
					Join thousands getting what they&apos;re owed.
				</Typography>

				<form
					className="w-full"
					onSubmit={handleSubmit((data) =>
						handleSubmitData(NextAuthProvidersEnum.CREDENTIALS, data)
					)}
				>
					<div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
						<Button
							size="full"
							onClick={() => handleSubmitData(NextAuthProvidersEnum.GOOGLE)}
							disabled={isLoading}
							icon={Icons.google}
							iconLeft
							label="Sign up with Google"
							className="!py-4 !bg-[#F4F4F4] !border-[#F4F4F4] !text-black hover:!bg-[#E8E8E8] hover:!border-[#E8E8E8]"
						/>
						<Button
							size="full"
							onClick={() => handleSubmitData(NextAuthProvidersEnum.MICROSOFT)}
							disabled={isLoading}
							icon={Icons.microsoft}
							iconLeft
							label="Sign up with Microsoft"
							className="!py-4 !bg-[#F4F4F4] !border-[#F4F4F4] !text-black hover:!bg-[#E8E8E8] hover:!border-[#E8E8E8]"
						/>
					</div>

					<Separator text="Or continue with email" className="my-6" />

					<div className="space-y-4 mb-4">
						<InputEmail
							name="email"
							title="Email"
							register={register}
							rules={rules.email}
							error={errors.email}
							setValueCustom={setValue}
						/>
						<div>
							<InputPassword
								name="password"
								title="Create a Password"
								register={register}
								validate={false}
								rules={rules.password}
								error={errors.password}
								hideErrorMessage
							/>
							{errors.password ? (
								<div className="mt-2">
									<ErrorMessageInput
										message={(errors?.password?.message as string) || ''}
									/>
								</div>
							) : (
								<Typography
									type="regular-10"
									className="text-opacity-black mt-2"
								>
									{passwordCriteria}
								</Typography>
							)}
						</div>
					</div>

					<div className={clsx({ hidden: isStaging || isDev })}>
						<Recaptcha
							onChangeRecaptcha={onChangeRecaptcha}
							siteKey={process.env.NEXT_PUBLIC_API_RECAPTCHA || ''}
						/>
					</div>

					<Typography
						type="regular-12"
						className="text-black text-center my-6 w-full"
					>
						By creating your account you are agreeing with our{' '}
						<Link href="/terms-of-use" target="_blank" className="underline">
							Terms of Use
						</Link>{' '}
						and{' '}
						<Link href="/privacy-policy" target="_blank" className="underline">
							Privacy Policy
						</Link>
						.
					</Typography>

					<Button
						type="submit"
						size="full"
						decoration="primary"
						label="Create an Account"
						loading={isLoading}
						className={clsx('w-full', {
							'hover:bg-[#B18EE0] !bg-[#B18EE0] !border-[#B18EE0]':
								!isValid || !isDirty,
						})}
						disabled={isLoading || !recaptchaValid}
					/>

					<div className="flex items-center justify-center gap-2 mt-8">
						<Icon
							src={Icons.shieldSecurity}
							width={16}
							height={16}
							className="text-primary shrink-0"
						/>
						<Typography type="regular-14" className="text-[#6B7280]">
							Your info is secure and only used to check eligibility.
						</Typography>
					</div>

					<Typography
						type="regular-16"
						className="my-4 mb-6 md:mb-0 xl:mt-6 text-center"
					>
						Already have an account?
						<Link href={`${AppRoutes.CLIENTAPP_AUTH_SIGN_IN}?from=register`}>
							{' '}
							<span className="text-primary hover:opacity-60 font-medium lg:font-semibold">
								Sign In
							</span>
						</Link>
					</Typography>
				</form>
			</div>
		</LayoutSignUp>
	);
};
